import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from '@tanstack/react-router';
import type { PlatformStats } from '@app/shared';
import {
  Cluster,
  Grid,
  Heading,
  Link,
  LineChart,
  PageHeader,
  Spinner,
  Stack,
  StatusBadge,
  Surface,
  Text,
} from '../ui';
import { api, ApiError } from '../lib/api';
import { qk } from '../lib/queryKeys';
import { BRAND_BLUE, HeroStat, Leaderboard, Section } from '../components/DashboardParts';
import { formatDayLabel, formatUsd } from '../lib/costChart';

/**
 * Platform dashboard — cross-tenant totals across every workspace (§14), operator-only and
 * reachable ONLY under Admin (`/app/admin/platform`, #332). Split off the workspace home so
 * this-workspace figures never sit next to platform totals.
 *
 * Operator gating is try-load-then-probe: the auth `me` payload carries no operator flag, so
 * we attempt the operator-gated `getPlatformStats` read. A non-operator's read 403s (401
 * anonymous) → a "restricted to platform operators" notice with NO totals (mirrors the
 * PollerActivity restricted pattern). Its existence never leaks: the Admin entry-point link
 * self-hides behind the same probe.
 */
export function PlatformDashboardPage() {
  const query = useQuery({
    queryKey: qk.platformStats(),
    queryFn: () => api.getPlatformStats(),
    retry: false,
  });
  const status = query.error instanceof ApiError ? query.error.status : undefined;

  if (query.isPending) {
    return (
      <Stack gap="6">
        <BackToAdmin />
        <PageHeader title="Platform — all workspaces" />
        <Cluster gap="2" align="center">
          <Spinner size="sm" />
          <Text size="sm" tone="muted">
            Checking access…
          </Text>
        </Cluster>
      </Stack>
    );
  }

  if (status === 403 || status === 401) {
    return (
      <Stack gap="6">
        <BackToAdmin />
        <PageHeader title="Platform" />
        <Surface bordered radius="lg" padding="5" data-testid="platform-dashboard-restricted">
          <Stack gap="2">
            <Heading level={2} size="sm">
              Restricted
            </Heading>
            <Text size="sm" tone="secondary">
              The platform dashboard is limited to platform operators.
            </Text>
          </Stack>
        </Surface>
      </Stack>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Stack gap="6">
        <BackToAdmin />
        <PageHeader title="Platform — all workspaces" />
        <Surface bordered radius="lg" padding="5">
          <Text size="sm" tone="danger" data-testid="platform-dashboard-error">
            Could not load platform stats. Try again shortly.
          </Text>
        </Surface>
      </Stack>
    );
  }

  return (
    <Stack gap="6" data-testid="platform-dashboard-page">
      <BackToAdmin />
      <PageHeader
        title="Platform — all workspaces"
        description="Cross-tenant totals across every workspace. Visible to platform operators only."
        actions={
          <StatusBadge tone="brand" label="Operator" data-testid="dashboard-platform-badge" />
        }
      />
      <PlatformStatsView stats={query.data} />
    </Stack>
  );
}

/** Back link to the Admin page (the entry point for this operator-only view). */
function BackToAdmin() {
  return (
    <Link
      as={RouterLink}
      muted
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {...({ to: '/app/admin' } as any)}
      data-testid="platform-dashboard-back"
    >
      ← Back to Admin
    </Link>
  );
}

function PlatformStatsView({ stats }: { stats: PlatformStats }) {
  const spendSeries = useMemo(
    () => stats.spendTrend.map((d) => ({ label: formatDayLabel(d.date), value: d.totalUsd })),
    [stats.spendTrend],
  );
  const spendTotal = useMemo(
    () => stats.spendTrend.reduce((sum, d) => sum + d.totalUsd, 0),
    [stats.spendTrend],
  );

  const t = stats.totals;

  return (
    <Surface bordered radius="lg" padding="6" data-testid="dashboard-platform-section">
      <Stack gap="6">
        <Text size="sm" tone="secondary">
          Cross-tenant totals across every workspace, over the last {stats.windowDays} days.
        </Text>

        <Cluster gap="8" align="end" wrap data-testid="dashboard-platform-totals">
          <HeroStat label="Users" value={t.users.toLocaleString()} hint="registered" testid="dashboard-platform-metric-users" />
          <HeroStat label="Workspaces" value={t.workspaces.toLocaleString()} hint="tenants" testid="dashboard-platform-metric-workspaces" />
          <HeroStat label="Active agents" value={t.activeAgents.toLocaleString()} hint="platform-wide" testid="dashboard-platform-metric-agents" />
          <HeroStat
            label="Runs"
            value={t.runsWindow.toLocaleString()}
            hint={`${t.runsAllTime.toLocaleString()} all-time`}
            testid="dashboard-platform-metric-runs"
          />
          {/* #332: the all-workspaces total spend, promoted to a prominent hero here so the
              platform total is unmistakable (it was only the spend-chart subtitle before). */}
          <HeroStat
            label="Total spend"
            value={formatUsd(spendTotal)}
            hint={`all workspaces · last ${stats.windowDays} days`}
            emphasis
            testid="dashboard-platform-metric-spend"
          />
        </Cluster>

        <Grid minChildWidth="20rem" gap="5">
          <Section title="Most active users" subtitle={`Last ${stats.windowDays} days`}>
            <Leaderboard
              testid="dashboard-most-active-users"
              emptyLabel="No user activity in this window yet."
              rows={stats.mostActiveUsers.map((u) => ({
                id: u.userId,
                primary: u.name || u.email,
                secondary: u.email,
                value: u.runCount,
              }))}
            />
          </Section>

          <Section title="Most active agents" subtitle={`Last ${stats.windowDays} days`}>
            <Leaderboard
              testid="dashboard-most-active-agents"
              emptyLabel="No agent activity in this window yet."
              rows={stats.mostActiveAgents.map((a) => ({
                id: a.agentId,
                primary: a.agentName,
                secondary: a.workspaceName,
                value: a.runCount,
              }))}
            />
          </Section>
        </Grid>

        <Section title="Platform spend" subtitle={`${formatUsd(spendTotal)} · last ${stats.windowDays} days`}>
          <LineChart
            data={spendSeries}
            height={180}
            color={BRAND_BLUE}
            formatValue={formatUsd}
            ariaLabel={`Total platform spend per day over the last ${stats.windowDays} days`}
            data-testid="dashboard-platform-spend"
          />
        </Section>

        {/* Quick chat, platform-wide — reported separately from agent runs (§14.5). */}
        <Section title="Quick chat" subtitle="All-time, across every workspace">
          <Cluster gap="8" align="end" wrap data-testid="dashboard-platform-chat">
            <HeroStat
              label="Sessions"
              value={stats.chat.sessions.toLocaleString()}
              hint="conversations"
              testid="dashboard-platform-chat-sessions"
            />
            <HeroStat
              label="Messages"
              value={stats.chat.messages.toLocaleString()}
              hint="user + assistant"
              testid="dashboard-platform-chat-messages"
            />
            <HeroStat
              label="Tool uses"
              value={stats.chat.toolUses.toLocaleString()}
              hint="actions taken"
              testid="dashboard-platform-chat-tooluses"
            />
          </Cluster>
        </Section>
      </Stack>
    </Surface>
  );
}
